<template>
  <!-- Dark in both themes, like the server logs page and a real terminal. -->
  <div class="flex flex-col h-app-screen bg-black text-gray-200">
    <!-- Header -->
    <div class="flex-shrink-0 bg-[#0d1117] border-b border-gray-800">
      <div class="flex items-center justify-between gap-4 px-4 sm:px-6 h-14 sm:h-auto sm:py-3.5">
        <div class="min-w-0">
          <h1 class="text-[22px] sm:text-xl font-bold tracking-tight leading-none text-white">Rails console</h1>
          <p class="hidden sm:block text-xs text-gray-500 mt-0.5 truncate" data-test="console-subtitle">
            <template v-if="store.session">
              {{ store.session.reason }} · opened {{ timeAgo(store.session.created_at) }}
            </template>
            <template v-else>Run Ruby on the {{ store.environment || 'server' }} app, with every command on record.</template>
          </p>
        </div>
        <div v-if="store.session" class="flex items-center gap-2 flex-shrink-0">
          <!-- How results are shown: the inspect text, JSON or a table -->
          <div class="inline-flex rounded-lg border border-gray-700 bg-black p-0.5" role="radiogroup" aria-label="Show results as" data-test="view-toggle">
            <button v-for="option in VIEWS" :key="option.value" type="button" role="radio" :aria-checked="view === option.value"
              @click="setView(option.value)" :data-test="`view-${option.value}`"
              class="px-2.5 py-1 text-xs font-semibold rounded-md transition-colors"
              :class="view === option.value ? 'bg-gray-800 text-white' : 'text-gray-500 hover:text-gray-200'">
              {{ option.label }}
            </button>
          </div>
          <span class="hidden sm:inline-flex items-center gap-1.5 px-2.5 py-1 text-xs font-semibold rounded-full border" data-test="mode-badge"
            :class="store.session.writable ? 'border-red-500/50 bg-red-500/10 text-red-300' : 'border-emerald-500/40 bg-emerald-500/10 text-emerald-300'">
            <span class="w-1.5 h-1.5 rounded-full" :class="store.session.writable ? 'bg-red-400' : 'bg-emerald-400'"></span>
            {{ store.session.writable ? 'Writes on' : 'Read only' }}
          </span>
          <button v-if="store.running" type="button" data-test="interrupt" @click="interrupt"
            class="px-3 py-1.5 sm:py-2 text-sm font-semibold rounded-full sm:rounded-lg border border-amber-500/50 bg-amber-500/10 text-amber-300 hover:bg-amber-500/20 transition-colors">
            Interrupt
          </button>
          <button v-if="store.live" type="button" data-test="close-console" @click="closeConsole"
            class="px-3 py-1.5 sm:py-2 text-sm font-semibold rounded-full sm:rounded-lg border border-gray-700 bg-gray-900 text-gray-200 hover:bg-gray-800 transition-colors">
            Close console
          </button>
        </div>
      </div>
    </div>

    <!-- Loading -->
    <div v-if="store.loading" class="flex-1 animate-pulse px-4 sm:px-6 py-4 space-y-3" data-test="console-skeleton">
      <div v-for="n in 8" :key="n" class="flex items-center gap-3">
        <div class="h-3 w-28 bg-gray-800 rounded flex-shrink-0"></div>
        <div class="h-3 bg-gray-900 rounded" :style="{ width: `${25 + ((n * 29) % 50)}%` }"></div>
      </div>
    </div>

    <!-- Error -->
    <div v-else-if="store.error" class="flex-1 flex flex-col items-center justify-center text-center px-4">
      <div class="w-16 h-16 rounded-full bg-red-900/30 flex items-center justify-center mb-4">
        <svg class="w-7 h-7 text-red-400" fill="none" stroke="currentColor" stroke-width="1.8" viewBox="0 0 24 24">
          <path stroke-linecap="round" stroke-linejoin="round" d="M12 9v3.75m9-.75a9 9 0 1 1-18 0 9 9 0 0 1 18 0Zm-9 3.75h.008v.008H12v-.008Z"/>
        </svg>
      </div>
      <h3 class="text-base font-semibold text-white">Something went wrong</h3>
      <p class="text-sm text-gray-400 mb-5">{{ store.error }}</p>
      <button type="button" @click="store.fetchCurrent()" class="px-4 py-2 bg-blue-600 hover:bg-blue-700 text-white text-sm font-semibold rounded-lg transition-colors">
        Try again
      </button>
    </div>

    <!-- Start a session -->
    <div v-else-if="!store.session" class="flex-1 overflow-y-auto px-4 py-10" data-test="start-panel">
      <form @submit.prevent="start" class="max-w-lg mx-auto rounded-2xl sm:rounded-xl border border-gray-800 bg-[#0d1117]">
        <div class="px-6 py-4 border-b border-gray-800">
          <h2 class="font-semibold text-white">Open a console</h2>
          <p class="text-sm text-gray-500 mt-0.5">
            A real Rails console on {{ store.environment }}. Variables stay alive until you close it. Every command is saved with your name before it runs.
          </p>
        </div>
        <div class="p-6 space-y-5">
          <div>
            <label for="console-reason" class="block text-sm font-medium text-gray-300 mb-1.5">Why are you opening it?</label>
            <input id="console-reason" v-model="reason" type="text" maxlength="500" required data-test="reason"
              placeholder="e.g. Check why booking 1234 did not sync"
              class="w-full px-3 py-2 text-sm rounded-lg border border-gray-700 bg-black text-gray-100 placeholder:text-gray-600 focus:ring-2 focus:ring-blue-500 focus:border-blue-500" />
          </div>

          <div class="flex items-start justify-between gap-4">
            <div>
              <p class="text-sm font-medium text-gray-300">Allow writes</p>
              <p class="text-xs mt-0.5" :class="writable ? 'text-red-400' : 'text-gray-500'">
                {{ writable ? 'Commands can change and delete data. Use read only unless you need to fix something.' : 'Off: any database write raises an error.' }}
              </p>
            </div>
            <button type="button" role="switch" :aria-checked="writable" data-test="writable-toggle" @click="writable = !writable"
              class="relative flex-shrink-0 w-11 h-6 rounded-full transition-colors" :class="writable ? 'bg-red-600' : 'bg-gray-700'">
              <span class="absolute top-0.5 left-0.5 w-5 h-5 rounded-full bg-white transition-transform" :class="writable ? 'translate-x-5' : ''"></span>
            </button>
          </div>

          <div v-if="store.confirmWith === 'two_factor'">
            <label for="console-code" class="block text-sm font-medium text-gray-300 mb-1.5">Two factor code</label>
            <input id="console-code" v-model="code" type="text" inputmode="numeric" autocomplete="one-time-code" required data-test="confirm-code"
              placeholder="6 digit code or a backup code"
              class="w-full px-3 py-2 text-sm font-mono tracking-widest rounded-lg border border-gray-700 bg-black text-gray-100 placeholder:font-sans placeholder:tracking-normal placeholder:text-gray-600 focus:ring-2 focus:ring-blue-500 focus:border-blue-500" />
          </div>
          <div v-else-if="store.confirmWith === 'password'">
            <label for="console-password" class="block text-sm font-medium text-gray-300 mb-1.5">Your password</label>
            <input id="console-password" v-model="password" type="password" autocomplete="current-password" required data-test="confirm-password"
              class="w-full px-3 py-2 text-sm rounded-lg border border-gray-700 bg-black text-gray-100 focus:ring-2 focus:ring-blue-500 focus:border-blue-500" />
          </div>
          <p v-else class="text-sm text-amber-300" data-test="confirm-none">
            Set a password or turn on two factor authentication in Settings before using the console.
          </p>

          <p v-if="store.startError" class="text-sm text-red-400" data-test="start-error">{{ store.startError }}</p>

          <div class="flex items-center justify-between gap-4 pt-1">
            <p class="text-xs text-gray-500">{{ store.liveCount }} of {{ store.maxLive }} consoles in use on the server</p>
            <button type="submit" data-test="start-console" :disabled="!canStart"
              class="px-4 py-2 text-sm font-semibold text-white bg-blue-600 hover:bg-blue-700 rounded-lg transition-colors disabled:opacity-50 disabled:cursor-not-allowed">
              {{ store.starting ? 'Starting' : 'Start console' }}
            </button>
          </div>
        </div>
      </form>
    </div>

    <!-- Terminal -->
    <template v-else>
      <div ref="scroller" class="flex-1 min-h-0 overflow-y-auto font-mono text-[13px] leading-5 px-4 sm:px-6 py-4 selection:bg-blue-500/40"
        @click="focusInput" data-test="terminal">
        <p class="text-gray-500 mb-3">
          Loading {{ store.environment }} environment (Rails {{ store.railsVersion }})
        </p>

        <div v-for="command in visibleCommands" :key="command.id" class="mb-3" data-test="command">
          <div class="flex">
            <span class="flex-shrink-0 select-none" :class="promptClass">{{ prompt }}&nbsp;</span>
            <pre class="min-w-0 whitespace-pre-wrap break-all"><template v-for="(part, i) in rubyTokens(command.code)" :key="i"><span :class="part.cls">{{ part.text }}</span></template></pre>
          </div>
          <!-- The SQL it ran, coloured like the server logs page -->
          <div v-if="command.queries?.length" class="text-[12px]" data-test="command-queries">
            <pre v-for="(query, q) in command.queries" :key="q" class="whitespace-pre-wrap break-all"><template
              v-for="(part, i) in sqlTokens(query)" :key="i"><span :class="part.cls">{{ part.text }}</span></template></pre>
          </div>
          <pre v-if="command.output" class="whitespace-pre-wrap break-all text-gray-300" data-test="command-output">{{ command.output }}</pre>
          <template v-if="command.result">
            <!-- JSON -->
            <pre v-if="view === 'json' && command.result_data" class="whitespace-pre-wrap break-all" data-test="command-json"><template
              v-for="(part, i) in jsonTokens(command.result_data)" :key="i"><span :class="part.cls">{{ part.text }}</span></template></pre>

            <!-- Table -->
            <div v-else-if="view === 'table' && command.result_data" class="max-h-96 overflow-auto rounded-lg border border-gray-800 my-1 font-sans" data-test="command-table">
              <table class="min-w-full text-xs">
                <thead class="sticky top-0 bg-[#0d1117]">
                  <tr>
                    <th v-for="(column, c) in tableOf(command.result_data).columns" :key="column"
                      :aria-sort="sortOf(command.id, c) === 'asc' ? 'ascending' : sortOf(command.id, c) === 'desc' ? 'descending' : 'none'"
                      class="border-b border-gray-800 p-0">
                      <!-- Click: ascending, descending, back to the original order -->
                      <button type="button" @click.stop="toggleSort(command.id, c)" data-test="sort-column"
                        class="w-full flex items-center gap-1 px-3 py-1.5 text-left font-semibold whitespace-nowrap transition-colors"
                        :class="sortOf(command.id, c) ? 'text-white' : 'text-gray-400 hover:text-gray-200'">
                        {{ column }}
                        <span class="text-[10px]" :class="sortOf(command.id, c) ? 'text-blue-400' : 'text-gray-700'">
                          {{ sortOf(command.id, c) === 'desc' ? '▼' : '▲' }}
                        </span>
                      </button>
                    </th>
                  </tr>
                </thead>
                <tbody class="divide-y divide-gray-900 font-mono">
                  <tr v-for="(row, r) in sortedRows(command)" :key="r" class="hover:bg-white/[0.04]">
                    <td v-for="(cell, c) in row" :key="c" class="px-3 py-1 max-w-[24rem] truncate whitespace-nowrap"
                      :class="isFieldTable(command.result_data) && c === 0 ? 'text-cyan-300' : cellClass(cell)" :title="cellText(cell)">
                      {{ cellText(cell) }}
                    </td>
                  </tr>
                </tbody>
              </table>
            </div>

            <!-- Console (and the fallback for values with no JSON form) -->
            <template v-else>
              <pre class="whitespace-pre-wrap break-all" data-test="command-result"><template
                v-for="(part, i) in resultTokens(command.result)" :key="i"><span :class="part.cls">{{ part.text }}</span></template></pre>
              <p v-if="view !== 'console'" class="text-[11px] text-gray-600 font-sans">This value has no {{ view === 'json' ? 'JSON' : 'table' }} form, so it is shown as console output.</p>
            </template>
            <p v-if="view !== 'console' && command.result_data?.truncated" class="text-[11px] text-gray-500 font-sans">
              Only the first {{ command.result_data.rows?.length || command.result_data.value?.length }} are shown.
            </p>
          </template>
          <div v-if="command.error_class && command.status === 'error'" data-test="command-error">
            <pre class="whitespace-pre-wrap break-all text-red-400"><span class="font-bold">{{ command.error_class }}</span>: {{ command.error_message }}</pre>
            <button v-if="command.backtrace" type="button" @click.stop="toggleBacktrace(command.id)"
              class="text-xs text-gray-500 hover:text-gray-300 font-sans">
              {{ openBacktraces.includes(command.id) ? 'Hide backtrace' : `Show backtrace (${command.backtrace.split('\n').length} lines)` }}
            </button>
            <pre v-if="openBacktraces.includes(command.id)" class="whitespace-pre-wrap break-all text-gray-500 text-xs mt-1">{{ command.backtrace }}</pre>
          </div>
          <pre v-if="command.status === 'interrupted'" class="text-amber-300" data-test="command-interrupted">{{ command.error_message || 'Interrupted.' }}</pre>
          <p class="flex items-center gap-2 text-[11px] font-sans mt-0.5">
            <template v-if="command.status === 'queued' || command.status === 'running'">
              <span class="inline-block w-3 h-3 rounded-full border-2 border-gray-600 border-t-blue-400 animate-spin"></span>
              <span class="text-gray-500">{{ command.status === 'queued' && store.session.status === 'starting' ? 'Waiting for the console to start' : 'Running' }}</span>
            </template>
            <template v-else-if="command.duration_ms !== null">
              <span class="text-green-400">{{ formatDuration(command.duration_ms) }}</span>
            </template>
          </p>
        </div>

        <p v-if="store.session.status === 'starting'" class="flex items-center gap-2 text-gray-500 mb-3" data-test="booting">
          <span class="inline-block w-3 h-3 rounded-full border-2 border-gray-600 border-t-blue-400 animate-spin"></span>
          Starting a Rails process. This takes a few seconds; you can type already.
        </p>

        <!-- Input -->
        <div v-if="store.live" class="flex" data-test="input-row">
          <span class="flex-shrink-0 select-none" :class="promptClass">{{ prompt }}&nbsp;</span>
          <div class="relative flex-1 min-w-0">
            <!-- Highlighted copy behind a transparent textarea: colours while typing -->
            <pre aria-hidden="true" class="absolute inset-0 whitespace-pre-wrap break-all pointer-events-none"><template
              v-for="(part, i) in rubyTokens(input)" :key="i"><span :class="part.cls">{{ part.text }}</span></template>{{ '\n' }}</pre>
            <textarea ref="inputEl" v-model="input" :rows="inputRows" spellcheck="false" autocapitalize="off" autocomplete="off"
              data-test="console-input" :disabled="!!store.running" @keydown="onKeydown" @input="onInput" @blur="onBlur"
              @click="closeSuggestions"
              class="relative w-full p-0 bg-transparent border-0 resize-none text-transparent caret-gray-100 focus:ring-0 font-mono text-[13px] leading-5 disabled:opacity-40"></textarea>

            <!-- Autocomplete, positioned at the cursor (monospace makes that simple arithmetic) -->
            <ul v-if="suggestions.items.length" role="listbox" data-test="suggestions"
              class="absolute z-20 w-72 max-h-64 overflow-y-auto rounded-lg border border-gray-700 bg-[#161b22] shadow-2xl py-1 font-mono text-[12.5px]"
              :style="{ top: `${suggestions.top}px`, left: `${suggestions.left}px` }">
              <li v-for="(item, i) in suggestions.items" :key="item.text" role="option" :aria-selected="i === suggestions.index"
                :ref="(el) => { if (i === suggestions.index && el) el.scrollIntoView({ block: 'nearest' }) }"
                @mousedown.prevent="accept(item)" @mouseenter="suggestions.index = i"
                class="flex items-center justify-between gap-3 px-3 py-1 cursor-pointer"
                :class="i === suggestions.index ? 'bg-blue-600/30' : ''">
                <span class="truncate"><span class="text-white font-semibold">{{ suggestions.prefix }}</span><span class="text-gray-300">{{ item.text.slice(suggestions.prefix.length) }}</span></span>
                <span class="flex-shrink-0 text-[10.5px] font-sans font-semibold uppercase tracking-wide" :class="KIND_CLASSES[item.kind] || 'text-gray-500'">{{ item.kind }}</span>
              </li>
            </ul>
          </div>
        </div>
        <p v-if="store.runError" class="text-red-400 text-xs font-sans mt-1" data-test="run-error">{{ store.runError }}</p>

        <!-- Closed -->
        <div v-if="!store.live" class="mt-4 rounded-xl border border-gray-800 bg-[#0d1117] px-4 py-3 font-sans flex items-center justify-between gap-4" data-test="closed-banner">
          <p class="text-sm text-gray-300">{{ closedMessage }}</p>
          <button type="button" @click="startAgain" class="flex-shrink-0 px-3 py-1.5 text-sm font-semibold text-white bg-blue-600 hover:bg-blue-700 rounded-lg transition-colors">
            Start a new console
          </button>
        </div>
      </div>

      <p v-if="store.live" class="flex-shrink-0 hidden sm:block px-6 py-2 border-t border-gray-900 text-[11px] text-gray-600">
        Enter runs · Shift+Enter new line · Tab completes · ↑ ↓ history · Ctrl+C interrupts · ⌘K or Ctrl+L clears the screen
      </p>
    </template>
  </div>
</template>

<script setup>
import { ref, computed, watch, nextTick, onMounted, onBeforeUnmount } from 'vue'
import { useAdminRailsConsoleStore } from 'stores/admin_rails_console.js'
import { tokenize, tokenizeJson } from './rubySyntax.js'
import { tokenize as tokenizeLog } from '../server_logs/logSyntax.js'

const COMMAND_POLL_MS = 300
const BOOT_POLL_MS = 1000
const HEALTH_POLL_MS = 15000
const COMPLETE_DEBOUNCE_MS = 60
const LINE_HEIGHT = 20 // leading-5
const KIND_CLASSES = {
  model: 'text-sky-300',
  class: 'text-sky-300',
  module: 'text-sky-400',
  constant: 'text-orange-300',
  variable: 'text-rose-300',
  ivar: 'text-rose-300',
  method: 'text-fuchsia-300',
  column: 'text-emerald-300',
  association: 'text-cyan-300',
  keyword: 'text-violet-300',
}
const HISTORY_KEY = 'rails_console_history'
const VIEW_KEY = 'rails_console_view'
const VIEWS = [
  { value: 'console', label: 'Console' },
  { value: 'json', label: 'JSON' },
  { value: 'table', label: 'Table' },
]
const HISTORY_MAX = 200
const CLOSE_REASONS = {
  closed_by_user: 'This console was closed.',
  exit: 'You exited the console.',
  idle: 'This console closed after 30 minutes without a command.',
  lifetime: 'This console reached its 8 hour limit and closed.',
  terminated: 'The console process was stopped, for example by a server restart.',
  worker_died: 'The console process stopped unexpectedly.',
  boot_timeout: 'The console process did not start in time.',
  launch_failed: 'The console process could not be started.',
}

const store = useAdminRailsConsoleStore()

const reason = ref('')
const writable = ref(false)
const code = ref('')
const password = ref('')
const input = ref('')
const view = ref(readView())
const scroller = ref(null)
const inputEl = ref(null)
const openBacktraces = ref([])
const clearedAt = ref(0) // Ctrl+L hides everything before this command position
const suggestions = ref({ items: [], prefix: '', index: 0, top: 0, left: 0 })
let completeTimer = null
let completeSeq = 0
let charWidth = 0
let history = []
let historyIndex = -1
let draft = ''
let bootTimer = null
let healthTimer = null
let unmounted = false

const canStart = computed(() => {
  if (store.starting || !reason.value.trim() || store.confirmWith === 'none') return false
  return store.confirmWith === 'two_factor' ? !!code.value.trim() : !!password.value
})

// Rails 7.2+ style prompt: calendar_app(prod)>, red for production.
const prompt = computed(() => {
  const env = { production: 'prod', development: 'dev' }[store.environment] || store.environment
  return `${store.appName}(${env})>`
})
const promptClass = computed(() => (store.environment === 'production' ? 'text-red-400' : 'text-green-400'))

const visibleCommands = computed(() => store.commands.filter((command) => command.position > clearedAt.value))
const inputRows = computed(() => Math.min(Math.max(input.value.split('\n').length, 1), 16))

const closedMessage = computed(() => {
  const status = store.session?.status
  const text = CLOSE_REASONS[store.session?.close_reason] || 'This console has closed.'
  return status === 'failed' ? text : `${text} Its commands stay in the audit log.`
})

function rubyTokens(text) {
  return tokenize(text)
}

function resultTokens(text) {
  return tokenize(text, { comments: false })
}

function readView() {
  try {
    const saved = localStorage.getItem(VIEW_KEY)
    return VIEWS.some((option) => option.value === saved) ? saved : 'console'
  } catch {
    return 'console'
  }
}

function setView(value) {
  view.value = value
  try {
    localStorage.setItem(VIEW_KEY, value)
  } catch {
    // private window: the choice lasts until the page closes
  }
}

// result_data from RailsConsole::ResultData: rows for records and hashes,
// otherwise a value.
function jsonTokens(data) {
  return tokenizeJson(JSON.stringify(data.type === 'records' ? data.rows : data.value, null, 2))
}

const isObject = (item) => item !== null && typeof item === 'object' && !Array.isArray(item)

// The table follows the shape of the result:
//   one object (a record, a hash)    field | value, a row per field
//   a list of objects                 a column per attribute, a row per object
//   a list of single values           one value column
//   a single value                    one cell
function tableOf(data) {
  if (data.type === 'records' && data.collection === false) return fieldRows(data.rows[0] || {})
  if (data.type === 'records') return objectRows(data.rows)

  const value = data.value
  if (Array.isArray(value) && value.length && value.every(isObject)) return objectRows(value)
  if (Array.isArray(value)) return { columns: ['value'], rows: value.map((item) => [item]) }
  if (isObject(value)) return fieldRows(value)
  return { columns: ['value'], rows: [[value]] }
}

// ---- Table sorting (per command, in the browser only) ----------------------

const sorts = ref({}) // command id → { column, direction: 'asc' | 'desc' }
const collator = new Intl.Collator(undefined, { numeric: true, sensitivity: 'base' })

function sortOf(commandId, column) {
  const sort = sorts.value[commandId]
  return sort && sort.column === column ? sort.direction : null
}

function toggleSort(commandId, column) {
  const current = sortOf(commandId, column)
  const next = { [null]: 'asc', asc: 'desc', desc: null }[current]
  const { [commandId]: _removed, ...rest } = sorts.value
  sorts.value = next ? { ...rest, [commandId]: { column, direction: next } } : rest
}

// nil always last; numbers by value, booleans false first, everything else
// as text with natural number order ("item 2" before "item 10").
function compareCells(a, b) {
  const aEmpty = a === null || a === undefined
  const bEmpty = b === null || b === undefined
  if (aEmpty || bEmpty) return aEmpty === bEmpty ? 0 : aEmpty ? 1 : -1
  if (typeof a === 'number' && typeof b === 'number') return a - b
  if (typeof a === 'boolean' && typeof b === 'boolean') return Number(a) - Number(b)
  return collator.compare(cellText(a), cellText(b))
}

function sortedRows(command) {
  const rows = tableOf(command.result_data).rows
  const sort = sorts.value[command.id]
  if (!sort) return rows
  const sign = sort.direction === 'asc' ? 1 : -1
  return [...rows].sort((a, b) => {
    const order = compareCells(a[sort.column], b[sort.column])
    // nil stays last in both directions
    const aEmpty = a[sort.column] === null || a[sort.column] === undefined
    const bEmpty = b[sort.column] === null || b[sort.column] === undefined
    return aEmpty || bEmpty ? order : order * sign
  })
}

function isFieldTable(data) {
  return tableOf(data).columns.join() === 'field,value'
}

function fieldRows(object) {
  return { columns: ['field', 'value'], rows: Object.entries(object) }
}

function objectRows(list) {
  const columns = [...new Set(list.flatMap((row) => Object.keys(row)))]
  return { columns, rows: list.map((row) => columns.map((column) => row[column])) }
}

function cellText(cell) {
  if (cell === null || cell === undefined) return 'nil'
  return typeof cell === 'object' ? JSON.stringify(cell) : String(cell)
}

function cellClass(cell) {
  if (cell === null || cell === undefined || cell === '[FILTERED]') return 'text-gray-600 italic'
  if (typeof cell === 'number') return 'text-orange-300'
  if (typeof cell === 'boolean') return 'text-violet-300'
  if (typeof cell === 'object') return 'text-gray-400'
  return 'text-gray-200'
}

// Same shape as a development log line, so logSyntax colours it the same way:
//   User Load (1.4ms)  SELECT "users".* ...  [["id", 1]]
function sqlTokens(query) {
  const binds = query.binds ? `  ${query.binds}` : ''
  return tokenizeLog(`  ${query.name} (${query.duration_ms}ms)  ${query.sql}${binds}`)
}

function formatDuration(ms) {
  if (ms < 1000) return `${ms} ms`
  if (ms < 60000) return `${(ms / 1000).toFixed(ms < 10000 ? 2 : 1)} s`
  return `${Math.floor(ms / 60000)} min ${Math.round((ms % 60000) / 1000)} s`
}

function timeAgo(iso) {
  const minutes = Math.round((Date.now() - new Date(iso).getTime()) / 60000)
  if (minutes < 1) return 'just now'
  if (minutes < 60) return `${minutes} min ago`
  return `${Math.floor(minutes / 60)} h ${minutes % 60} min ago`
}

function toggleBacktrace(id) {
  openBacktraces.value = openBacktraces.value.includes(id)
    ? openBacktraces.value.filter((x) => x !== id)
    : [...openBacktraces.value, id]
}

// History: this session's commands first, then earlier sessions from this
// browser (localStorage, best effort).
function loadHistory() {
  let saved = []
  try {
    saved = JSON.parse(localStorage.getItem(HISTORY_KEY) || '[]')
  } catch {
    saved = []
  }
  history = [...saved, ...store.commands.map((command) => command.code)].filter((c, i, all) => all.lastIndexOf(c) === i)
}

function remember(entry) {
  history = [...history.filter((c) => c !== entry), entry].slice(-HISTORY_MAX)
  try {
    localStorage.setItem(HISTORY_KEY, JSON.stringify(history))
  } catch {
    // private window or storage full: history just stays in memory
  }
}

async function start() {
  if (!canStart.value) return
  const ok = await store.start({
    reason: reason.value.trim(),
    writable: writable.value,
    code: code.value.trim() || undefined,
    password: password.value || undefined,
  })
  code.value = ''
  password.value = ''
  if (!ok) return
  loadHistory()
  watchSession()
  await focusInput()
}

async function submit() {
  closeSuggestions()
  const text = input.value
  if (!text.trim() || store.running || !store.live) return
  remember(text)
  historyIndex = -1
  input.value = ''
  const command = await store.run(text)
  await scrollToBottom()
  if (command) await followCommand(command.id)
  else input.value = text // refused (closed console, too long): keep what was typed
}

async function followCommand(id) {
  while (!unmounted) {
    if (await store.pollCommand(id)) break
    await new Promise((resolve) => setTimeout(resolve, COMMAND_POLL_MS))
  }
  // `exit` or a close from elsewhere ends the session; show it straight away.
  await store.refreshSession()
  await scrollToBottom()
  await focusInput()
}

function interrupt() {
  if (store.running && !store.running.local) store.interrupt(store.running.id)
}

function clearScreen() {
  clearedAt.value = store.commands.at(-1)?.position || 0
}

// Cmd+K (macOS) or Ctrl+L clears the screen from anywhere on the page, like a
// terminal. The commands stay in the session and the audit log.
function onWindowKeydown(event) {
  const key = event.key.toLowerCase()
  if (!store.session) return
  if ((event.metaKey && key === 'k') || (event.ctrlKey && key === 'l')) {
    event.preventDefault()
    clearScreen()
    focusInput()
  }
}

async function closeConsole() {
  await store.close()
}

async function startAgain() {
  stopTimers()
  clearedAt.value = 0
  await store.fetchCurrent()
}

// ---- Autocomplete -------------------------------------------------------

function closeSuggestions() {
  completeSeq += 1 // an answer still on its way is now stale
  suggestions.value = { ...suggestions.value, items: [], index: 0 }
}

function onBlur() {
  closeSuggestions()
}

// Asks after a short pause in typing, only when the cursor ends a name,
// a `.` or a `::`. Answers that arrive after newer typing are dropped.
function onInput() {
  clearTimeout(completeTimer)
  const el = inputEl.value
  const before = el ? input.value.slice(0, el.selectionStart) : ''
  if (!/(?:[A-Za-z_@][\w]*[?!]?|\.|::)$/.test(before) || store.session?.status !== 'ready') {
    closeSuggestions()
    return
  }
  const seq = ++completeSeq
  completeTimer = setTimeout(async () => {
    const answer = await store.complete(before)
    if (seq !== completeSeq || !answer?.items?.length) {
      if (seq === completeSeq) closeSuggestions()
      return
    }
    suggestions.value = { items: answer.items, prefix: answer.prefix, index: 0, ...caretPosition(before) }
  }, COMPLETE_DEBOUNCE_MS)
}

// Where the cursor is, in pixels inside the input: the font is monospace, so
// it is the column times the character width, counting wrapped lines.
function caretPosition(before) {
  const el = inputEl.value
  if (!charWidth) {
    const probe = document.createElement('span')
    probe.className = 'font-mono text-[13px]'
    probe.style.visibility = 'hidden'
    probe.style.position = 'absolute'
    probe.textContent = 'x'.repeat(100)
    document.body.appendChild(probe)
    charWidth = probe.getBoundingClientRect().width / 100 || 7.8
    probe.remove()
  }
  const columns = Math.max(Math.floor((el?.clientWidth || 600) / charWidth), 1)
  const lines = before.split('\n')
  const rows = lines.reduce((sum, line, i) => sum + (i < lines.length - 1 ? Math.floor(line.length / columns) + 1 : 0), 0)
  const last = lines.at(-1)
  const row = rows + Math.floor(last.length / columns)
  const column = last.length % columns
  const left = Math.min(column * charWidth, Math.max((el?.clientWidth || 600) - 288, 0))
  return { top: (row + 1) * LINE_HEIGHT + 2, left }
}

async function accept(item) {
  const el = inputEl.value
  const caret = el.selectionStart
  const start = caret - suggestions.value.prefix.length
  input.value = input.value.slice(0, start) + item.text + input.value.slice(caret)
  closeSuggestions()
  await nextTick()
  const position = start + item.text.length
  el.focus()
  el.setSelectionRange(position, position)
}

// Keys for the open list: ↑ ↓ move, Tab or Enter picks, Esc closes.
function onSuggestionKey(event) {
  const list = suggestions.value
  if (!list.items.length) return false
  if (event.key === 'ArrowDown' || event.key === 'ArrowUp') {
    const step = event.key === 'ArrowDown' ? 1 : -1
    list.index = (list.index + step + list.items.length) % list.items.length
  } else if (event.key === 'Tab' || (event.key === 'Enter' && !event.shiftKey)) {
    accept(list.items[list.index])
  } else if (event.key === 'Escape') {
    closeSuggestions()
  } else {
    return false
  }
  event.preventDefault()
  return true
}

function onKeydown(event) {
  if (onSuggestionKey(event)) return
  const el = event.target
  if (event.key === 'Enter' && !event.shiftKey) {
    event.preventDefault()
    submit()
  } else if (event.key === 'ArrowUp' && !el.value.slice(0, el.selectionStart).includes('\n')) {
    if (!history.length) return
    event.preventDefault()
    if (historyIndex === -1) draft = input.value
    historyIndex = historyIndex === -1 ? history.length - 1 : Math.max(historyIndex - 1, 0)
    input.value = history[historyIndex]
  } else if (event.key === 'ArrowDown' && historyIndex !== -1 && !el.value.slice(el.selectionEnd).includes('\n')) {
    event.preventDefault()
    historyIndex = historyIndex + 1 >= history.length ? -1 : historyIndex + 1
    input.value = historyIndex === -1 ? draft : history[historyIndex]
  } else if (event.ctrlKey && event.key === 'c' && store.running && el.selectionStart === el.selectionEnd) {
    event.preventDefault()
    interrupt()
  }
}

async function focusInput() {
  await nextTick()
  if (window.getSelection()?.toString()) return // do not steal a selection the user is copying
  inputEl.value?.focus()
}

async function scrollToBottom() {
  await nextTick()
  if (scroller.value) scroller.value.scrollTop = scroller.value.scrollHeight
}

// While booting, check every second; afterwards every 15 seconds, so an idle
// timeout or a dead worker shows up without typing.
function watchSession() {
  stopTimers()
  bootTimer = setInterval(async () => {
    if (store.session?.status !== 'starting') {
      clearInterval(bootTimer)
      bootTimer = null
      return
    }
    await store.refreshSession()
  }, BOOT_POLL_MS)
  healthTimer = setInterval(() => {
    if (store.live && !store.running && !document.hidden) store.refreshSession()
  }, HEALTH_POLL_MS)
}

function stopTimers() {
  clearInterval(bootTimer)
  clearInterval(healthTimer)
  bootTimer = null
  healthTimer = null
}

watch(() => store.commands.length, scrollToBottom)
watch(() => store.session?.status, (status) => {
  if (status === 'ready') focusInput()
  if (status && !['starting', 'ready'].includes(status)) stopTimers()
})

onMounted(async () => {
  window.addEventListener('keydown', onWindowKeydown)
  await store.fetchCurrent()
  if (!store.session) return
  loadHistory()
  watchSession()
  await scrollToBottom()
  // Reloading the page mid command: keep following it.
  if (store.running) await followCommand(store.running.id)
  else await focusInput()
})

onBeforeUnmount(() => {
  clearTimeout(completeTimer)
  window.removeEventListener('keydown', onWindowKeydown)
  unmounted = true
  stopTimers()
})
</script>
